/**
 * Missing personal info on a tenant — the "Needs attention" list.
 *
 * Defined once because three surfaces render it (the tenant profile's
 * attention strip, the tenant list's red line under the name, and the room
 * view's current-tenant card) and three hand-rolled copies drifted in both
 * field order and wording. The stage badge and this warning travel together:
 * a tenant stuck mid-journey is usually stuck because something here is blank.
 */

/** Canonical order — the same order the profile's Personal Information reads. */
export interface TenantInfoInput {
  name?: string | null;
  dateOfBirth?: string | null;
  email?: string | null;
  phone?: string | null;
}

/** A placeholder is as good as blank: the em dash is what the UI prints. */
function blank(v: string | null | undefined): boolean {
  const s = String(v ?? "").trim();
  return s === "" || s === "—" || s === "Tenant";
}

/**
 * The missing field labels, in canonical order. Empty when nothing is missing.
 */
export function resolveTenantInfoGaps(input: TenantInfoInput): string[] {
  const missing: string[] = [];
  if (blank(input.name)) missing.push("name");
  if (blank(input.dateOfBirth)) missing.push("date of birth");
  if (blank(input.email)) missing.push("email");
  if (blank(input.phone)) missing.push("phone");
  return missing;
}

export type Translate = (key: string, fallback?: string) => string;

/** Canonical field id → its translation key. Field ids themselves (returned
 * by `resolveTenantInfoGaps`) stay untranslated — only the rendered label
 * goes through `t()`. */
export const FIELD_LABEL_KEYS: Record<string, string> = {
  name: "admin.tenantInfoGaps.field.name",
  "date of birth": "admin.tenantInfoGaps.field.dateOfBirth",
  email: "admin.tenantInfoGaps.field.email",
  phone: "admin.tenantInfoGaps.field.phone",
};

/**
 * One-line form: "Missing personal info: date of birth". Null when complete.
 *
 * `maxNamed` caps how many fields are spelled out before collapsing into
 * "+N more" — a bare guest booking is often missing several, and the compact
 * surfaces (list rows, room cards) have one line to say so.
 *
 * Pass the `t` from `useLanguage()` to get a translated label; omitted, this
 * returns the plain English form (used by tests and any non-UI caller).
 */
export function tenantInfoGapLabel(
  input: TenantInfoInput,
  maxNamed = Number.POSITIVE_INFINITY,
  t?: Translate
): string | null {
  const missing = resolveTenantInfoGaps(input);
  if (!missing.length) return null;
  const fieldLabel = (field: string) =>
    t ? t(FIELD_LABEL_KEYS[field] ?? field, field) : field;
  const shown = missing.slice(0, maxNamed).map(fieldLabel).join(", ");
  const extra = missing.length - maxNamed;
  const named =
    extra > 0
      ? `${shown} ${
          t
            ? t("admin.tenantInfoGaps.more", "+{n} more").replace(
                "{n}",
                String(extra)
              )
            : `+${extra} more`
        }`
      : shown;
  const prefix = t
    ? t("admin.tenantInfoGaps.prefix", "Missing personal info")
    : "Missing personal info";
  return `${prefix}: ${named}`;
}
